import Backbone from 'backbone'
import BoxOptions from 'scoreboard/components/box-options'
import $ from 'jquery'
import _ from 'lodash'
import StrongConfirmPrompt from 'scoreboard/components/strong-confirm-prompt'
import i18n from 'scoreboard/components/i18n'
import appDisplay from 'scoreboard/components/app-display'
import template from './new-record-options.hbs'
import FormView from 'scoreboard/components/form-view'

/**
 * A toggle for switching between adding/updating records on a dataset whenever
 * new data is imported or replacing all existing data with the new data
 */
const NewRecordOptions = Backbone.View.extend({
	tagName: 'form',

	events: {
		'click li': 'onClickNewRecordOption'
	},

	/**
	 *
	 * @param {String} [options.replaceAllRecords] the initial option to select
	 * @param {Boolean} [options.renderAsDialog] renders these options as a
	 * 											 standalone dialog
	 * @param {Boolean} [options.noConfirmation=false] if true,the confirmation dialog
	 *                                          when selecting 'Replace All Records'
	 *                                          is not displayed
	 * @param {Boolean} [options.hasPrimaryKeyField=false] indicates whether the dataset
	 * has a defined primaryKeyFieldId
	 */
	initialize: function(options) {
		this.options = options || {}
		// #36046 make sure this.replaceAllRecords is always set
		this.replaceAllRecords = this.options.replaceAllRecords

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			// an option is always selected, so the user doesn't have to manually
			// select one
			allowCleanSubmission: true
		})
	},

	render: function() {
		if (this.options.renderAsDialog) {
			this.$el.empty().append(template())
		}

		let appendRecordTitle = 'admin.import.spreadsheet.append'
		if (!this.options.hasPrimaryKeyField) {
			appendRecordTitle += '.short'
		}

		this.boxOptions = new BoxOptions({
			availableOptions: [
				{ value: 'append', title: appendRecordTitle, icon: 'replace-record'},
				{ value: 'replace', title: 'admin.import.spreadsheet.replace', icon: 'add-and-update-record'}
			]
		})
		if (this.options.renderAsDialog) {
			this.$('.options-container').html(this.boxOptions.el)
		} else {
			this.$el.html(this.boxOptions.el)
		}
		this.boxOptions.render()

		// Set the initial selection based on what was selected last time
		this.setValue(this.options.replaceAllRecords)


		if (this.options.renderAsDialog) {
			this.formView.start()
		}
		return this
	},

	onClickNewRecordOption: function(e) {
		this.handleOptionSelected(e)
	},

	/**
	 * handler for when an option is clicked
	 *
	 * @param e the click event
	 * @param {optionSelectedCallback} callback
	 */
	handleOptionSelected: function(e, callback) {
		this.setValue($(e.currentTarget).data('value') === 'replace')
		if (!this.options.noConfirmation) {
			this.confirmReplaceIfNeeded(callback)
		}

		callback && callback(this.replaceAllRecords)
	},

	/**
	 * @param currentValue #37294 if set, we use this as the original value instead
	 * 					   of what was passed in with the options. Useful if the
	 * 					   state of this value has changed outside of this component
	 */
	confirmReplaceIfNeeded(callback, currentValue = null) {
		if (currentValue === null) {
			currentValue = _.isNil(this.options.replaceAllRecords) ? false :
				this.options.replaceAllRecords
		}
		if (this.replaceAllRecords && !currentValue) {
			// Show warning dialog if the user is opting to overwrite data
			const confirmPrompt = new StrongConfirmPrompt({
				dialogTitle: i18n.get('admin.import.spreadsheet.replace'),
				message: i18n.get('admin.import.spreadsheet.replace.confirmation'),
				prompt: i18n.get('admin.import.spreadsheet.overwrite.prompt'),
				confirmationText: i18n.get('admin.import.spreadsheet.overwrite'),
				confirmationButton: i18n.get('admin.import.spreadsheet.replace'),
				showUndoneWarning: true
			})

			confirmPrompt.once('confirm', () => this._handleResponse(true, callback))
			confirmPrompt.once('cancel', () => this._handleResponse(false, callback))
			this.confirmDialog = appDisplay.dialog(confirmPrompt)
		} else {
			this._handleResponse(true, callback)
		}
	},

	/**
	 * Sets the UI according to the final selection value
	 *
	 * @private
	 */
	_handleResponse(confirmed, callback) {
		if (this.confirmDialog) {
			this.confirmDialog.close()
			this.confirmDialog.remove()
		}

		callback && callback(this.replaceAllRecords, !confirmed)
	},

	setValue(replaceAllRecords) {
		const value = _.isNil(replaceAllRecords) ? false : replaceAllRecords
		this.replaceAllRecords = replaceAllRecords
		this.boxOptions.val(value ? 'replace' : 'append')
	},

	submit: function() {
		this.confirmReplaceIfNeeded((replaceAllRecords, cancelled) => {
			if (cancelled) {
				// #35715 make sure the form properly re-enables if the users clicks
				// cancel on the overwrite confirmation dialog
				this.formView.start()
			} else {
				this.trigger('done', replaceAllRecords)
			}
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.boxOptions, this.formView], 'remove')
	}
})

export default NewRecordOptions

NewRecordOptions.__filename__ = 'datasets/new-record-options.js'
